import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES, AxwayTokenFetcherService } from '@ptl/xhr';
import { ProducerCreateReportResponse, ProducerReportDetailsResponse, ProducerReportDownloadResponse, ProducerReportType, ReportDropdownResponse } from './interfaces/reports-service.interface';
import { UserInfoStateStructure, LOGIN_INFO_REDUCER_NAME, UserInfo, EntitlementsHelperService, BaseAgency } from '@ptl/account-management';
import { ReducerUtilityService, REDUCER_TYPES } from '@ptl/state';
import { ContentStorageService } from '@ptl/global-components';
import { API_ENDPOINTS } from 'src/app/shared/constants/api-endpoints.constant';
// producer-reports.service.ts
//import { AxwayPostPasswordV4 } from '@ptl/xhr'; // use your actual package name


@Injectable({
  providedIn: 'root'
})
export class ProducerReportsService {

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private cookieService: CookieService,
    private httpClient: HTTPWrapperContext,
    private entitlementsHelperService: EntitlementsHelperService,
    private contentService: ContentStorageService,
    private axwayTokenFetcherService: AxwayTokenFetcherService
  ) { }

  async loadDropdownValues(): Promise<ReportDropdownResponse> {
    const dropdownUrl = this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.REPORTS.AGENCY_FETCH_CONFIGURATION, {});
    const userInfoState: UserInfoStateStructure = this.getUserInfoState();
    const userInfo: UserInfo = userInfoState.userInfo;
    const selectedAgency = await this.entitlementsHelperService.getSelectedAgencyPromise();

    const payload = {
      baseMessage: {},
      payload: {
        userRole: userInfo.role,
        region: selectedAgency.region
      }
    };

    return this.makeRequest(dropdownUrl, payload);
  }

  async createReport(type: ProducerReportType, format: string, cycle: string): Promise<ProducerCreateReportResponse> {
    const createUrl = this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.REPORTS.AGENCY_CREATE_REPORT, {});
    const userInfoState: UserInfoStateStructure = this.getUserInfoState();
    const selectedAgency = await this.entitlementsHelperService.getSelectedAgencyPromise();

    const payload = {
      payload: {
        loginId: userInfoState.userId,
        agencyId: selectedAgency.agencyId,
        agencyName: selectedAgency.agencyName,
        region: selectedAgency.region,

        reportTypeId: type.reportTypeId,
        type: type.reportType,
        format: format,
        cycle: cycle
      }
    };

    return this.makeRequest(createUrl, payload);
  }

  async getReportsList(): Promise<ProducerReportDetailsResponse> {
    const reportsUrl = this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.REPORTS.AGENCY_FETCH_REPORT, {});
    const userInfoState: UserInfoStateStructure = this.getUserInfoState();
    const selectedAgency = await this.entitlementsHelperService.getSelectedAgencyPromise();

    const payload = {
      payload: {
        loginId: userInfoState.userId,
        agencyId: selectedAgency.agencyId,
        region: selectedAgency.region,
        regionId: this.getRegionId(userInfoState.userInfo, selectedAgency)
      }
    };

    return this.makeRequest(reportsUrl, payload);
  }

  /*
    The download can be tested in Tenv7 with the type "ACA Individual Book of Business"
  */
  async downloadReport(reportId: string) {
    // const downloadUrl = '/cmcrst/x-services/agencyReports/downloadReport';
    const downloadUrl = this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.REPORTS.AGENCY_DOWNLOAD_REPORT, {});
    const payload = { baseMessage: {}, payload: { reportId } };

    let response: ProducerReportDownloadResponse = await this.makeRequestBlob(downloadUrl, payload);

    if (response.status && response.status.toLowerCase() === "success") {
      const blob = this.getBlobData(response.base64data, response.mimeType);
      const blobUrl = window.URL.createObjectURL(blob);

      var link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.setAttribute('download', response.filename);

      //In FF link must be added to DOM to be clicked
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // This is necessary to convert the base 64 data into a byte array before downloading it
  private getBlobData(base64raw: string, contentType: string) {
    var sliceSize = 512;
    var base64decoded = atob(base64raw);
    var base64bytes = [];

    for (var offset = 0; offset < base64decoded.length; offset += sliceSize) {
      var slice = base64decoded.slice(offset, offset + sliceSize);

      var sliceArray = new Array(slice.length);
      for (var i = 0; i < slice.length; i++) {
        sliceArray[i] = slice.charCodeAt(i);
      }

      var sliceBytes = new Uint8Array(sliceArray);

      base64bytes.push(sliceBytes);
    }

    return new Blob(base64bytes, { type: contentType });
  }

  // All reports services use the same method of POST and require the LTPA token
  private async makeRequest(url: string, payload: any): Promise<any> {
    //const tokenResponse = await this.oauthCallwithAxwayV4();
    //const tokenResponse =  this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    const userInfoState: UserInfoStateStructure = this.getUserInfoState();
    const host = window.location.hostname;
    let env = '';
    if (host.indexOf('env') >= 0) {
      env = 't' + host.substring(host.indexOf('env'), host.indexOf('.'));
    }
    const requestConfig = new RequestConfigImplementation({
      url: url,
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        //'Authorization': `Bearer ${tokenResponse['access_token']}`,
        'x-hmhs-test-backend': `wasinter${env}.highmark.com`,
        'fromDP': userInfoState.userId
      }),
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest(requestConfig);
  }

  private async makeRequestBlob(downloadUrl: string, payload: any): Promise<any> {
      const host = window.location.hostname;
      let env = '';
      if (host.indexOf('env') >= 0) {
        env = 't' + host.substring(host.indexOf('env'), host.indexOf('.'));
      }

      const requestConfig = new RequestConfigImplementation({
        method: REQUEST_METHODS.POST,
        url: downloadUrl,
        headers: new HttpHeaders({
          'x-hmhs-test-backend': `wasinter${env}.highmark.com`,
        }),
        POST_OR_PUT_PAYLOAD: payload
      });
  
      this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
      return this.httpClient.makeRequest(requestConfig);
  }

  /*
    Helper methods for agency values
  */
  private getUserInfoState(): UserInfoStateStructure {
    const USER_INFO_STATE_NAME = `${REDUCER_TYPES.APPLICATION_STATE}.${LOGIN_INFO_REDUCER_NAME}`;
    const userInfoState: UserInfoStateStructure = this.reducerUtilityService.getGlobalStateItem(USER_INFO_STATE_NAME);

    return userInfoState;
  }

  private getRegionId(userInfo: UserInfo, selectedAgency: BaseAgency): number {
    let curRegion = selectedAgency.region;
    return userInfo.regions.filter((region) => region.region == curRegion)[0].code;
  }
}
